import { InfoCircleOutlined } from '@ant-design/icons';
import { type ILanguageType } from '@mahawi/eshop-common/src/types';
import { Popover, Radio, Space, theme, Tooltip } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../hooks/use-app-dispatch';
import { EHotkey, useHotkey } from '../hooks/use-hotkey';
import { type RootState } from '../reducers';
import {
  nextLanguage,
  previousLanguage,
  setLanguage,
} from '../reducers/language/reducer';
import { SPACING } from '../theme/tokens';
import { HotkeyList } from './keyboard-shortcuts';

const infoContent: React.ReactElement = (
  <HotkeyList hotkeys={[EHotkey.NEXT_LANGUAGE, EHotkey.PREVIOUS_LANGUAGE]} />
);

function LanguageButtons({
  languageTypeCodeError = [],
}: {
  languageTypeCodeError?: string[];
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const languageLanguages = useSelector((s: RootState) => s.Language.languages);
  const languageTypeCode = useSelector(
    (s: RootState) => s.Language.languageType?.code,
  );

  const { token } = theme.useToken();

  const onChangeSelect = useCallback(
    (languageType: ILanguageType): void => {
      dispatch(setLanguage(languageType));
    },
    [dispatch],
  );

  useHotkey(
    [EHotkey.NEXT_LANGUAGE, EHotkey.PREVIOUS_LANGUAGE],
    (hotkey: EHotkey): void => {
      dispatch(
        hotkey === EHotkey.NEXT_LANGUAGE ? nextLanguage() : previousLanguage(),
      );
    },
  );

  const buttons = useMemo(
    () =>
      languageLanguages?.map((language: ILanguageType): React.ReactElement => (
        <Tooltip title={language.name} key={language.code}>
          <Radio.Button
            value={language.code}
            style={{
              color: languageTypeCodeError.includes(language.code)
                ? token.colorError
                : token.colorText,
            }}
            onClick={(): void => onChangeSelect(language)}
          >
            {language.code}
          </Radio.Button>
        </Tooltip>
      )),
    [
      languageLanguages,
      languageTypeCodeError,
      token.colorError,
      token.colorText,
      onChangeSelect,
    ],
  );

  return (
    <Space size={SPACING.lg} align="start">
      <Radio.Group value={languageTypeCode}>{buttons}</Radio.Group>
      <Popover title="Shortcuts" trigger="hover" content={infoContent}>
        <InfoCircleOutlined />
      </Popover>
    </Space>
  );
}

export default LanguageButtons;
